import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Zap, Receipt, Headphones, MapPin, ShieldCheck, Star, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ReservationSearchForm from "@/components/ReservationSearchForm";
import CampaignCarousel from "@/components/CampaignCarousel";
import VehicleCard from "@/components/VehicleCard";
import BookingDialog from "@/components/BookingDialog";
import api from "@/lib/api";
import { WHY_VOYACAR, STEPS, buildWhatsAppUrl } from "@/lib/site";
import { FAQS } from "@/lib/faqs";
import { useSettings } from "@/context/SettingsContext";

const ICONS = { sparkles: Sparkles, zap: Zap, receipt: Receipt, headphones: Headphones, "map-pin": MapPin, "shield-check": ShieldCheck };

const SectionHead = ({ eyebrow, title, desc, center }) => (
  <div className={`mb-10 ${center ? "text-center max-w-2xl mx-auto" : ""}`}>
    {eyebrow && <div className="text-xs font-semibold uppercase tracking-wider text-[#A11016] mb-2">{eyebrow}</div>}
    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#171717]">{title}</h2>
    {desc && <p className="mt-3 text-base text-neutral-500 leading-relaxed">{desc}</p>}
  </div>
);

export default function Home() {
  const { settings } = useSettings();
  const [vehicles, setVehicles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    document.title = "VoyaCar Rental | İstanbul Araç Kiralama";
    api.get("/vehicles", { params: { sort: "popular" } }).then((r) => setVehicles(r.data.slice(0, 6))).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const waUrl = buildWhatsAppUrl(settings.whatsapp, "Merhaba VoyaCar Rental, hızlı rezervasyon yapmak istiyorum.");

  return (
    <div>
      {/* CAMPAIGNS — header'a yapışık, tam genişlik */}
      <CampaignCarousel />

      {/* SEARCH */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="home-search-section">
        <ReservationSearchForm variant="hero" />
      </section>

      {/* TRUST BADGES */}
      <div className="border-y border-neutral-200 bg-white" data-testid="trust-badges">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm font-medium text-[#171717]">
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#A11016]" /> Sigortalı araçlar</span>
          <span className="flex items-center gap-2"><Receipt className="h-4 w-4 text-[#A11016]" /> Şeffaf süreç</span>
          <span className="flex items-center gap-2"><Headphones className="h-4 w-4 text-[#A11016]" /> 7/24 destek</span>
        </div>
      </div>

      {/* POPULAR VEHICLES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <SectionHead eyebrow="Filomuz" title="Popüler Araçlarımız" desc="En çok tercih edilen bakımlı ve konforlu araçlar." />
          <Link to="/araclar" className="text-sm font-semibold text-[#A11016] hover:underline flex items-center gap-1 mb-10">Tümünü Gör <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {vehicles.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} onBook={setBooking} />)}
        </div>
      </section>

      {/* WHY VOYACAR */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead center eyebrow="Neden VoyaCar?" title="Ayrıcalıklı Kiralama Deneyimi" desc="Yolculuğunuzu güvenli ve keyifli kılan avantajlarımız." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_VOYACAR.map((w) => {
              const Icon = ICONS[w.icon];
              return (
                <div key={w.title} className="rounded-2xl border border-neutral-200 p-6 hover:shadow-md hover:border-[#A11016]/30 transition-all" data-testid={`why-card-${w.icon}`}>
                  <div className="h-12 w-12 rounded-xl bg-[#FFF0F2] flex items-center justify-center mb-4"><Icon className="h-6 w-6 text-[#A11016]" /></div>
                  <h3 className="text-lg font-semibold text-[#171717]">{w.title}</h3>
                  <p className="mt-2 text-sm text-neutral-500 leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <SectionHead center eyebrow="Nasıl Çalışır?" title="3 Adımda Aracınız Hazır" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {STEPS.map((s) => (
            <div key={s.n} className="relative bg-white rounded-2xl border border-neutral-200 p-8 text-center" data-testid={`step-${s.n}`}>
              <div className="mx-auto h-14 w-14 rounded-full bg-[#A11016] text-white text-xl font-bold flex items-center justify-center mb-5">{s.n}</div>
              <h3 className="text-lg font-semibold text-[#171717]">{s.title}</h3>
              <p className="mt-2 text-sm text-neutral-500">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <SectionHead center eyebrow="Müşteri Yorumları" title="VoyaCar Deneyimi" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-neutral-200 p-6" data-testid={`review-${r.id}`}>
              <div className="flex gap-0.5 mb-3">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-[#A11016] text-[#A11016]" />)}</div>
              <p className="text-sm text-neutral-600 leading-relaxed">"{r.text}"</p>
              <div className="mt-4 pt-4 border-t border-neutral-100">
                <div className="font-semibold text-[#171717] text-sm">{r.name}</div>
                <div className="text-xs text-neutral-400">{r.location}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead center eyebrow="SSS" title="Sıkça Sorulan Sorular" />
          <Accordion type="single" collapsible className="w-full">
            {FAQS.slice(0, 6).map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`home-faq-${i}`}>
                <AccordionTrigger className="text-left text-[#171717] font-medium hover:text-[#A11016]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-neutral-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* WHATSAPP CTA */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-[#A11016] p-8 sm:p-12 text-center text-white overflow-hidden relative">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">WhatsApp'tan Saniyeler İçinde Rezervasyon</h2>
            <p className="mt-3 text-white/90 max-w-xl mx-auto">Aracınızı seçin, tarihinizi belirtin; gerisini biz halledelim. Hemen mesaj gönderin.</p>
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="home-whatsapp-cta">
              <Button className="mt-6 h-12 px-8 bg-white text-[#A11016] hover:bg-neutral-100 font-semibold">WhatsApp ile Yaz</Button>
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT + MAP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-white rounded-2xl border border-neutral-200 p-8">
            <SectionHead eyebrow="İletişim" title="Bize Ulaşın" />
            <ul className="space-y-4">
              <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-[#A11016]" /><a href={`tel:${settings.phone_link}`} className="text-neutral-700 hover:text-[#A11016] font-medium">{settings.phone}</a></li>
              <li className="flex items-center gap-3"><MapPin className="h-5 w-5 text-[#A11016]" /><span className="text-neutral-700">{settings.service_city}</span></li>
              <li className="flex items-center gap-3"><Headphones className="h-5 w-5 text-[#A11016]" /><span className="text-neutral-700">{settings.working_hours}</span></li>
            </ul>
            <Link to="/iletisim"><Button className="mt-6 bg-[#A11016] hover:bg-[#820D12] text-white" data-testid="home-contact-button">İletişim Sayfası</Button></Link>
          </div>
          <div className="rounded-2xl overflow-hidden border border-neutral-200 min-h-[300px]">
            <iframe title="Voyacar Rent A Car Harita" src="https://www.google.com/maps?q=Atatürk+Mahallesi+Alemdağ+Caddesi+Çeşme+Sokak+No+3+Ümraniye+İstanbul&output=embed" className="w-full h-full min-h-[300px]" loading="lazy" />
          </div>
        </div>
      </section>

      <BookingDialog open={!!booking} onOpenChange={(o) => !o && setBooking(null)} vehicle={booking} />
    </div>
  );
}
